// Published-content extras for one faculty member's detail page — the
// fields staff.ts has no room for and no source for (qualifications,
// publications, courses taught, research areas, professional memberships,
// external profile links). See publishedFacultyPhotos.ts for the sibling
// hook that does the same additive-override thing for photos on the
// directory/department pages; this is the richer, single-person version
// FacultyProfile.tsx needs.
//
// As of 2026-09-17 almost every one of these fields is empty for almost
// everyone published — webadmin's faculty module carries the schema but
// hasn't really been filled in yet (one record even has literal "test"/
// "test2" placeholder values in it). That's fine: every section below
// renders nothing when its array is empty, the same "an empty table is a
// gap, a wrong table is a harm" rule the rest of this codebase already
// follows (see EXAM_FEES in lib/fees.ts) — nothing here is invented to fill
// the gap.
import { useCollection } from "./siteContent";
import { normalizePublishedSlug } from "./publishedFacultySlugAliases";

export interface PublishedCourse {
  code?: string;
  title: string;
}

export interface PublishedPublication {
  title: string;
  venue?: string;
  year?: number | string;
}

export interface PublishedProfileLink {
  label?: string;
  url: string;
}

export interface PublishedFacultyProfile {
  slug: string;
  name: string;
  isHod: boolean;
  department: { code: string; name: string; slug: string } | null;
  program: { code: string; name: string; slug: string } | null;
  qualifications: string[];
  researchAreas: string[];
  memberships: string[];
  coursesTaught: PublishedCourse[];
  publications: PublishedPublication[];
  profileLinks: PublishedProfileLink[];
}

/**
 * The one published faculty record matching this slug, or undefined if
 * webadmin hasn't published this person at all. Narrower than the raw
 * collection item — only the fields this page actually renders.
 */
export function usePublishedFacultyProfile(slug: string | undefined): PublishedFacultyProfile | undefined {
  const { items } = useCollection("faculty");
  if (!slug) return undefined;
  const records = items as unknown as Array<Record<string, unknown>>;
  const r = records.find((rec) => normalizePublishedSlug(String(rec.slug)) === slug);
  if (!r) return undefined;
  return {
    slug,
    name: String(r.name ?? ""),
    isHod: Boolean(r.isHod),
    department: (r.department as PublishedFacultyProfile["department"]) ?? null,
    program: (r.program as PublishedFacultyProfile["program"]) ?? null,
    qualifications: asStringArray(r.qualifications),
    researchAreas: asStringArray(r.researchAreas),
    memberships: asStringArray(r.memberships),
    coursesTaught: Array.isArray(r.coursesTaught) ? (r.coursesTaught as PublishedCourse[]) : [],
    publications: Array.isArray(r.publications) ? (r.publications as PublishedPublication[]) : [],
    profileLinks: asProfileLinks(r.profileLinks),
  };
}

/** Defensive: webadmin's shape for these three is unconfirmed (always empty
 * so far) — accept a plain string array if that's what shows up, otherwise
 * treat anything unrecognized as empty rather than risk rendering `[object
 * Object]`. */
function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

/**
 * Not a plain cast like the other array fields above: webadmin's own record
 * carries each link's URL under `href` (confirmed against live published
 * data, e.g. naveen-kumar-k-r's Google Scholar / BlogSpot links), not `url`.
 * A bare `as PublishedProfileLink[]` typed it correctly but left `l.url`
 * undefined at runtime, so FacultyProfile.tsx rendered `<a href={undefined}>`
 * — a link that looked right and did nothing on click. Normalize the field
 * name here so the rest of the codebase can keep using `url`, matching every
 * other link shape (EventItem, ExamResult, …).
 */
function asProfileLinks(value: unknown): PublishedProfileLink[] {
  if (!Array.isArray(value)) return [];
  const out: PublishedProfileLink[] = [];
  for (const v of value) {
    if (!v || typeof v !== "object") continue;
    const href = (v as Record<string, unknown>).href;
    if (typeof href !== "string" || !href.trim()) continue;
    const label = (v as Record<string, unknown>).label;
    out.push({ url: href.trim(), label: typeof label === "string" ? label.trim() : undefined });
  }
  return out;
}
